import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Volume2, X } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const VoiceWelcome = () => {
  const { t, lang } = useLanguage();
  const [showPill, setShowPill] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const blobRef = useRef(null);
  const blobLang = useRef(null);
  const armed = useRef(false);

  const loadAudio = async () => {
    if (!blobRef.current || blobLang.current !== lang) {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/voice/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: t.common.welcome }),
      });
      if (!res.ok) throw new Error("tts");
      blobRef.current = await res.blob();
      blobLang.current = lang;
    }
    return blobRef.current;
  };

  const play = async () => {
    if (!armed.current || playing) return;
    try {
      const blob = await loadAudio();
      if (!armed.current) return;
      const audio = new Audio(URL.createObjectURL(blob));
      audioRef.current = audio;
      setPlaying(true);
      audio.onended = () => { setPlaying(false); };
      await audio.play();
      armed.current = false;
      setShowPill(false);
      sessionStorage.setItem("xs-welcomed", "1");
    } catch {
      // autoplay bloccato: mostra la pillola
      setPlaying(false);
      setShowPill(true);
    }
  };

  useEffect(() => {
    const returning = localStorage.getItem("xs-visited");
    localStorage.setItem("xs-visited", "1");
    if (!returning || sessionStorage.getItem("xs-welcomed")) return undefined;
    armed.current = true;

    const onGesture = (e) => {
      // i click sull'assistente non attivano il benvenuto: è l'assistente a parlare
      if (e.target.closest?.('[data-testid="whatsapp-widget-button"]') || e.target.closest?.('[data-testid="whatsapp-panel"]')) return;
      play();
      window.removeEventListener("pointerdown", onGesture);
      window.removeEventListener("keydown", onGesture);
    };
    window.addEventListener("pointerdown", onGesture);
    window.addEventListener("keydown", onGesture);
    const timer = setTimeout(() => {
      if (armed.current) setShowPill(true);
      window.removeEventListener("pointerdown", onGesture);
      window.removeEventListener("keydown", onGesture);
    }, 8000);

    return () => {
      window.removeEventListener("pointerdown", onGesture);
      window.removeEventListener("keydown", onGesture);
      clearTimeout(timer);
      audioRef.current?.pause();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Se l'utente apre l'assistente, il benvenuto si ferma: mai due voci insieme
  useEffect(() => {
    const stop = () => {
      armed.current = false;
      audioRef.current?.pause();
      setPlaying(false);
      setShowPill(false);
      sessionStorage.setItem("xs-welcomed", "1");
    };
    window.addEventListener("xs:open-whatsapp", stop);
    return () => window.removeEventListener("xs:open-whatsapp", stop);
  }, []);

  return (
    <AnimatePresence>
      {showPill && !playing && (
        <motion.button
          onClick={play}
          data-testid="welcome-voice-play-button"
          className="fixed bottom-6 left-6 z-[64] glass-panel rounded-full px-5 py-3 flex items-center gap-2.5 text-sm text-neon-cyan hover:text-white transition-colors"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
        >
          <Volume2 size={16} className="animate-pulse" /> {t.common.welcomeCta}
        </motion.button>
      )}
      {playing && (
        <motion.div
          data-testid="welcome-voice-playing"
          className="fixed bottom-6 left-6 z-[64] glass-panel rounded-full px-5 py-3 flex items-center gap-3 text-sm text-white/70"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
        >
          <span className="w-2 h-2 rounded-full bg-neon-cyan animate-pulse" />
          <span className="hud-label text-[10px] uppercase">XS</span>
          <button
            onClick={() => { audioRef.current?.pause(); setPlaying(false); sessionStorage.setItem("xs-welcomed", "1"); }}
            data-testid="welcome-voice-stop"
            aria-label="Interrompi benvenuto vocale"
            className="text-white/50 hover:text-white"
          >
            <X size={14} />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default VoiceWelcome;
